// Connect Higgsfield (F9b route A): the second way in beside "ask your Claude". Filmsquare signs in to the person's
// own Higgsfield (read-only, revocable in Settings → Connections), lists their projects per Higgsfield workspace, and
// stages the same import plan Claude would; after Import it reads the work itself. Every state is quiet: not set up
// on this server, not connected, asks to connect again. Outline buttons in the entry card (the white primary stays
// with the screen it sits in); in the picker dialog "Arrange N projects" is the one primary.

import { useCallback, useEffect, useMemo, useState } from "react";
import { BRAND } from "../../brand.ts";
import {
  connectErrorText,
  connectionState,
  forgetHiggsfieldReturn,
  type HiggsfieldApi,
  type HiggsfieldConnection,
  type HiggsfieldReturn,
  type HiggsfieldState,
  type HiggsfieldWorkspace,
  higgsfieldApi,
  isReconnect,
  onHiggsfieldReturn,
  peekHiggsfieldReturn,
  returnPath,
} from "../../data/higgsfield-api.ts";
import { atLeast } from "../../lib.ts";
import { formatRoute, hashOfPath } from "../../route.ts";
import { errText, useApp } from "../app.ts";
import { Icon } from "../icons.tsx";
import { Button, Dialog, Skeleton } from "../kit.tsx";
import {
  EMPTY_PICK,
  groupState,
  type PickerState,
  pickSummary,
  prunePick,
  toggleGroup,
  togglePick,
} from "./picker.ts";

export function reloadAfterHiggsfieldReturn(returned: HiggsfieldReturn | null, reload: () => void): void {
  if (returned?.outcome === "connected") reload();
}

import "./import.css";

/** The caller's connection, read once per workspace; null while it loads (or when it could not be read). */
export function useHiggsfieldConnection(): {
  hf: HiggsfieldApi;
  conn: HiggsfieldConnection | null;
  state: HiggsfieldState | null;
  /** the connection could not be read (not "not configured": that is a state) */
  failed: string | null;
  reload: () => void;
} {
  const { api, ws } = useApp();
  const hf = useMemo(() => higgsfieldApi(api, ws), [api, ws]);
  const [conn, setConn] = useState<HiggsfieldConnection | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [n, setN] = useState(0);
  const reload = useCallback(() => setN((x) => x + 1), []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: n reads it again
  useEffect(() => {
    let live = true;
    setFailed(null);
    hf.connection().then(
      (c) => live && setConn(c),
      (e) => {
        if (!live) return;
        setConn(null);
        setFailed(errText(e));
      },
    );
    return () => {
      live = false;
    };
  }, [hf, n]);
  useEffect(
    () =>
      onHiggsfieldReturn(() => {
        reloadAfterHiggsfieldReturn(peekHiggsfieldReturn(), reload);
      }),
    [reload],
  );
  return { hf, conn, state: connectionState(conn), failed, reload };
}

/** Send the browser to Higgsfield's sign-in; it comes back to the screen the person is on. */
export async function startConnect(hf: HiggsfieldApi): Promise<void> {
  const { authorizeUrl } = await hf.connect(returnPath(window.location.hash));
  window.location.assign(authorizeUrl);
}

/** What Higgsfield's sign-in came back with, taken once by the screen that shows it. */
export function useHiggsfieldReturn(): HiggsfieldReturn | null {
  const [r, setReturn] = useState(peekHiggsfieldReturn);
  useEffect(() => onHiggsfieldReturn(() => setReturn(peekHiggsfieldReturn())), []);
  useEffect(() => {
    if (r) forgetHiggsfieldReturn();
  }, [r]);
  return r;
}

/** The connect button with its own busy and error state. */
export function ConnectButton({
  hf,
  label,
  variant = "outline",
}: {
  hf: HiggsfieldApi;
  label: string;
  variant?: "outline" | "primary";
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <Button
        variant={variant}
        size="sm"
        disabled={busy}
        aria-busy={busy || undefined}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await startConnect(hf);
          } catch (e) {
            setError(errText(e));
            setBusy(false);
          }
        }}
      >
        {busy ? "Opening Higgsfield…" : label}
      </Button>
      {error && (
        <p className="imp-check conflict" role="alert">
          {error}
        </p>
      )}
    </>
  );
}

const CONNECTIONS = formatRoute({ view: "settings", page: "connections" });

/**
 * Home's Higgsfield strip, for whoever may stage an import: always there (not only while an import runs), in the
 * studio's work register. Connected: which account, Choose projects, and Switch account (Settings → Connections).
 * Not connected or asking again: one Connect button. Nothing while it loads or when this server has no connections.
 */
export function HiggsfieldHome() {
  const { role } = useApp();
  const { hf, conn, state, reload } = useHiggsfieldConnection();
  const [picking, setPicking] = useState(false);
  if (!atLeast(role, "producer") || state === null || state === "unconfigured") return null;
  return (
    <aside
      className="imp-banner imp-hf-home"
      aria-label="Import from Higgsfield"
      data-testid={`hf-home-${state}`}
    >
      <span className="imp-banner-mark" aria-hidden="true">
        <Icon name="upload" size={14} />
      </span>
      <p className="imp-banner-text">
        <strong>Import from Higgsfield</strong>
        <span>
          {state === "connected"
            ? `Connected${conn?.account ? ` as ${conn.account}` : ""} · read-only, no credits used`
            : state === "reconnect"
              ? "Higgsfield asks you to connect again"
              : "Connect your Higgsfield account and bring your projects in. Read-only, no credits used."}
        </span>
      </p>
      {state === "connected" ? (
        <>
          <a className="link imp-hf-home-switch" href={CONNECTIONS}>
            Switch account
          </a>
          <Button size="sm" icon="upload" onClick={() => setPicking(true)}>
            Choose projects
          </Button>
        </>
      ) : (
        <ConnectButton
          hf={hf}
          label={state === "reconnect" ? "Reconnect Higgsfield" : "Connect Higgsfield"}
        />
      )}
      {picking && (
        <HiggsfieldPicker
          hf={hf}
          onClose={() => setPicking(false)}
          onReconnect={() => {
            setPicking(false);
            reload();
          }}
        />
      )}
    </aside>
  );
}

/** The entry card's second way in. Nothing while the connection loads or when this server has no connections. */
export function HiggsfieldPath() {
  const { hf, state, reload } = useHiggsfieldConnection();
  const back = useHiggsfieldReturn();
  const [picking, setPicking] = useState(false);
  // just came back connected: open the picker straight away
  useEffect(() => {
    if (back?.outcome === "connected" && state === "connected") setPicking(true);
  }, [back, state]);
  // still loading, or the connection could not be read: the Claude path stays, nothing about Higgsfield shows
  if (state === null) return null;
  if (state === "unconfigured")
    return (
      <p className="imp-entry-foot imp-hf-quiet" data-testid="hf-unconfigured">
        Connecting Higgsfield directly is not set up on this server. Your Claude can still bring the work in.
      </p>
    );
  return (
    <div className="imp-hf" data-testid={`hf-${state}`}>
      <p className="imp-entry-step">
        <span>
          <strong>Or connect Higgsfield.</strong> {BRAND.name} reads your projects itself, read-only. No
          Claude needed.
        </span>
      </p>
      {back?.outcome === "error" && state !== "connected" && (
        <p className="imp-check note" role="status">
          {connectErrorText(back.reason)}
        </p>
      )}
      {state === "reconnect" && (
        <p className="imp-check note" role="status">
          Higgsfield asks you to connect again.
        </p>
      )}
      <div className="imp-hf-do">
        {state === "connected" ? (
          <>
            <Button size="sm" icon="upload" onClick={() => setPicking(true)}>
              Choose projects
            </Button>
            <span className="k-meta">
              <Icon name="check" size={11} /> Higgsfield connected
            </span>
          </>
        ) : (
          <ConnectButton
            hf={hf}
            label={state === "reconnect" ? "Reconnect Higgsfield" : "Connect Higgsfield"}
          />
        )}
      </div>
      {picking && (
        <HiggsfieldPicker
          hf={hf}
          onClose={() => setPicking(false)}
          onReconnect={() => {
            setPicking(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * Under "Waiting for your Claude" on a plan Claude staged: Filmsquare can read the same projects itself. Connected:
 * one button that starts the read; not connected: Connect Higgsfield, which comes back to this screen. Nothing while
 * the connection loads or when this server has no connections.
 */
export function ReadInstead({ onRead }: { onRead: () => Promise<void> }) {
  const { hf, state } = useHiggsfieldConnection();
  const back = useHiggsfieldReturn();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (state === null || state === "unconfigured") return null;
  return (
    <div className="imp-hf" data-testid={`hf-instead-${state}`}>
      <p className="imp-entry-step">
        <span>
          <strong>Or let {BRAND.name} read it.</strong> With your Higgsfield connection, read-only. No Claude
          needed.
        </span>
      </p>
      {back?.outcome === "error" && state !== "connected" && (
        <p className="imp-check note" role="status">
          {connectErrorText(back.reason)}
        </p>
      )}
      <div className="imp-hf-do">
        {state === "connected" ? (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onRead();
              } catch (e) {
                setError(errText(e));
                setBusy(false);
              }
            }}
          >
            {busy ? "Starting…" : "Read it from Higgsfield"}
          </Button>
        ) : (
          <ConnectButton
            hf={hf}
            label={state === "reconnect" ? "Reconnect Higgsfield" : "Connect Higgsfield"}
          />
        )}
      </div>
      {error && (
        <p className="imp-check conflict" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

type Listing =
  | { s: "loading" }
  | { s: "error"; text: string }
  | { s: "reconnect" }
  | { s: "ready"; workspaces: HiggsfieldWorkspace[] };

/** The live list of the person's Higgsfield projects, ticked per workspace, staged as an import plan. */
export function HiggsfieldPicker({
  hf,
  onClose,
  onReconnect,
}: {
  hf: HiggsfieldApi;
  onClose: () => void;
  /** Higgsfield no longer takes the sign-in: the caller shows the reconnect state */
  onReconnect: () => void;
}) {
  const { go } = useApp();
  const [list, setList] = useState<Listing>({ s: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [pick, setPick] = useState<PickerState>(EMPTY_PICK);
  const [staging, setStaging] = useState(false);
  const [stageError, setStageError] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt asks again
  useEffect(() => {
    let live = true;
    setList({ s: "loading" });
    hf.workspaces().then(
      (workspaces) => {
        if (!live) return;
        setList({ s: "ready", workspaces });
        setPick((p) => prunePick(p, workspaces));
      },
      (e) => live && setList(isReconnect(e) ? { s: "reconnect" } : { s: "error", text: errText(e) }),
    );
    return () => {
      live = false;
    };
  }, [hf, attempt]);

  const workspaces = list.s === "ready" ? list.workspaces : [];
  const sum = pickSummary(pick, workspaces);
  const arrange = async () => {
    if (!sum.body || staging) return;
    setStaging(true);
    setStageError(null);
    try {
      const plan = await hf.stage(sum.body.sourceWorkspaceId, sum.body.projectIds);
      const at = hashOfPath(plan.arrangeUrl ?? "");
      go({ view: "import", planId: at ? at.replace(/^#\/import\//, "") : plan.id });
    } catch (e) {
      if (isReconnect(e)) {
        setList({ s: "reconnect" });
      } else setStageError(errText(e));
      setStaging(false);
    }
  };

  return (
    <Dialog
      title="Choose Higgsfield projects"
      eyebrow={<span className="imp-eyebrow">Higgsfield → {BRAND.name}</span>}
      lead={<span>Made in Higgsfield · no credits used</span>}
      onClose={onClose}
      className="imp imp-pick"
      footer={
        <div className="imp-foot">
          <div className="imp-foot-text">
            <p className="imp-foot-sum">
              <span className="imp-foot-free">
                {sum.reason ??
                  "You arrange them as episodes or productions next. Nothing comes in before Import."}
              </span>
            </p>
            {stageError && (
              <p className="imp-check conflict" role="alert">
                {stageError}
              </p>
            )}
          </div>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!sum.body || staging || list.s !== "ready"}
            aria-busy={staging || undefined}
            onClick={() => void arrange()}
          >
            {staging ? "Staging…" : sum.label}
          </Button>
        </div>
      }
    >
      <div className="imp-body">
        {list.s === "loading" ? (
          <div className="imp-loading" aria-busy="true">
            <span className="sr-only" role="status">
              Reading your Higgsfield projects
            </span>
            <Skeleton h={40} />
            <Skeleton h={120} />
          </div>
        ) : list.s === "reconnect" ? (
          <div className="imp-failed" role="status">
            <p className="imp-failed-title">Higgsfield asks you to connect again</p>
            <p className="imp-failed-text">The sign-in {BRAND.name} had no longer works. Nothing was read.</p>
            <ConnectButton hf={hf} label="Reconnect Higgsfield" />
            <Button variant="text" size="sm" onClick={onReconnect}>
              Not now
            </Button>
          </div>
        ) : list.s === "error" ? (
          <div className="imp-failed" role="alert">
            <p className="imp-failed-title">Your Higgsfield projects could not be read</p>
            <p className="imp-failed-text">{list.text}</p>
            <Button onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
          </div>
        ) : workspaces.every((w) => w.projects.length === 0) ? (
          <p className="imp-progress-lead">There are no projects in your Higgsfield account yet.</p>
        ) : (
          <PickerList workspaces={workspaces} pick={pick} onPick={setPick} />
        )}
      </div>
    </Dialog>
  );
}

function PickerList({
  workspaces,
  pick,
  onPick,
}: {
  workspaces: readonly HiggsfieldWorkspace[];
  pick: PickerState;
  onPick: (p: PickerState) => void;
}) {
  return (
    <div className="imp-pick-groups">
      {workspaces
        .filter((w) => w.projects.length > 0)
        .map((w) => {
          const g = groupState(pick, w);
          const waiting = g === "waiting";
          const name = w.name?.trim() || "Higgsfield workspace";
          return (
            <fieldset key={w.id} className={`imp-pick-group${waiting ? " waiting" : ""}`} disabled={waiting}>
              <legend className="imp-pick-legend">
                <label className="imp-pick-row head">
                  <input
                    type="checkbox"
                    className="imp-check-box"
                    checked={g === "all"}
                    ref={(el) => {
                      if (el) el.indeterminate = g === "some";
                    }}
                    onChange={() => onPick(toggleGroup(pick, workspaces, w.id))}
                    aria-label={`All projects in ${name}`}
                  />
                  <span className="imp-pick-name">{name}</span>
                  <span className="k-meta">
                    {w.projects.length} {w.projects.length === 1 ? "project" : "projects"}
                  </span>
                </label>
              </legend>
              {waiting && (
                <p className="imp-pick-hint">
                  One Higgsfield workspace per import. Clear the ticks to pick here.
                </p>
              )}
              <ul className="imp-pick-list">
                {w.projects.map((p) => (
                  <li key={p.id}>
                    <label className="imp-pick-row">
                      <input
                        type="checkbox"
                        className="imp-check-box"
                        checked={pick.workspaceId === w.id && pick.selected.includes(p.id)}
                        onChange={() => onPick(togglePick(pick, workspaces, w.id, p.id))}
                      />
                      <span className="imp-pick-name">{p.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          );
        })}
    </div>
  );
}

/**
 * On the progress screen of a plan Filmsquare reads itself: why reading did not start, quietly, with the way on
 * (Try again, or Reconnect when Higgsfield no longer takes the sign-in).
 */
export function HiggsfieldReadNote({
  hf,
  error,
  onRetry,
}: {
  hf: HiggsfieldApi;
  error: unknown;
  onRetry: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const reconnect = isReconnect(error);
  return (
    <div className="imp-claude" role="status">
      <p className="imp-claude-text">
        {reconnect
          ? `Higgsfield asks you to connect again before ${BRAND.name} can read these projects.`
          : `${BRAND.name} could not start reading Higgsfield: ${errText(error)}`}
      </p>
      <div className="imp-hf-do">
        {reconnect ? (
          <ConnectButton hf={hf} label="Reconnect Higgsfield" />
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={async () => {
              setBusy(true);
              try {
                await onRetry();
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Starting…" : "Try again"}
          </Button>
        )}
      </div>
    </div>
  );
}
